/**
 * TrustScore breakdown — title map, status thresholds, and pill styling.
 *
 * The API's factors.tail_breakdown / factors.fleet_breakdown each carry a
 * `components` map keyed by short codes (IHT, MRT, OST for tail; CSF, FSF,
 * ORF for fleet). Each component is { value, description } — the
 * description is a technical string like:
 *   "Tail Incident History - 0 incident(s) found for this aircraft"
 * so we split on " - " to derive the title AND the body copy.
 *
 * The pill (Flagged / Acceptable / Optimal) is per-component because each
 * has its own semantic — some are penalties (lower = better) and some
 * are bonuses (higher = better). The formula that produces the final
 * breakdown score is the source of truth for which is which:
 *   tail:  100 - MRT + OST - 5*IHT   (OST bonus, MRT + IHT penalties)
 *   fleet: 100 - ORF + FSF - CSF     (FSF bonus, ORF + CSF penalties)
 */

export const STATUS = Object.freeze({
  OPTIMAL: 'optimal',
  ACCEPTABLE: 'acceptable',
  FLAGGED: 'flagged',
});

// Component-level thresholds. Chosen from the sample data value ranges
// (IHT 0-33, MRT 0, OST 10, CSF 10, FSF 0-5, ORF 0-13.82) and the
// business-intuition each component's description conveys.
const COMPONENT_THRESHOLDS = {
  // penalties (lower = better)
  IHT: { direction: 'lowerBetter', optimalMax: 0, acceptableMax: 5 },
  MRT: { direction: 'lowerBetter', optimalMax: 5, acceptableMax: 15 },
  CSF: { direction: 'lowerBetter', optimalMax: 3, acceptableMax: 7 },
  ORF: { direction: 'lowerBetter', optimalMax: 5, acceptableMax: 15 },
  // bonuses (higher = better)
  OST: { direction: 'higherBetter', optimalMin: 8, acceptableMin: 4 },
  FSF: { direction: 'higherBetter', optimalMin: 0.01, acceptableMin: 0 },
};

/**
 * Fixed title map (matches the description prefixes verified across the
 * full sample dataset). Fallback: derive from description prefix.
 */
const COMPONENT_TITLES = {
  IHT: 'Tail Incident History',
  MRT: 'Tail Maintenance Risk',
  OST: 'Tail Ownership Status',
  CSF: 'Fleet Certification Penalty',
  FSF: 'Fleet Financial Score',
  ORF: 'Fleet Operational Risk',
};

export const getComponentTitle = (key, description) => {
  if (COMPONENT_TITLES[key]) return COMPONENT_TITLES[key];
  if (typeof description === 'string' && description.includes(' - ')) {
    return description.split(' - ')[0].trim();
  }
  return key || 'Score Component';
};

/**
 * Description body — the part AFTER " - " in the component description.
 * Falls back to the whole description when the delimiter is absent so we
 * never show an empty body.
 */
export const getComponentBody = (description) => {
  if (typeof description !== 'string' || !description) return '';
  const idx = description.indexOf(' - ');
  if (idx === -1) return description;
  return description.slice(idx + 3).trim();
};

/**
 * Resolve status for a single component's numeric value using its
 * per-key threshold. Unknown keys default to lower-is-better with a
 * generic band — safer than assuming direction.
 */
export const getComponentStatus = (key, value) => {
  const v = Number(value);
  if (!Number.isFinite(v)) return STATUS.ACCEPTABLE;

  const spec = COMPONENT_THRESHOLDS[key] || {
    direction: 'lowerBetter',
    optimalMax: 5,
    acceptableMax: 15,
  };

  if (spec.direction === 'lowerBetter') {
    if (v <= spec.optimalMax) return STATUS.OPTIMAL;
    if (v <= spec.acceptableMax) return STATUS.ACCEPTABLE;
    return STATUS.FLAGGED;
  }
  // higherBetter
  if (v >= spec.optimalMin) return STATUS.OPTIMAL;
  if (v >= spec.acceptableMin) return STATUS.ACCEPTABLE;
  return STATUS.FLAGGED;
};

/**
 * Overall trust status derived from the overall_score number. Mirrors
 * the existing getScoreBg thresholds in TrustScore.jsx so the hero card
 * pill agrees with the score-circle color.
 */
export const getOverallStatus = (overallScore) => {
  const v = Number(overallScore);
  if (!Number.isFinite(v)) return STATUS.ACCEPTABLE;
  if (v >= 80) return STATUS.OPTIMAL;
  if (v >= 60) return STATUS.ACCEPTABLE;
  return STATUS.FLAGGED;
};

/**
 * Card / pill palette per status. Kept as Tailwind class strings so
 * consumers just spread them into className.
 *
 * `cardBg` / `cardBorder` are the flat variants (used by the hero card
 * which sits on a lighter surface). `cardGradientBg` fades the tint
 * left→white so a row of six factor cards reads as one visual system
 * — matches the Figma. `cardGradientBorder` runs the same fade on the
 * border via the standard Tailwind "padded gradient wrapper + rounded
 * inner" trick (border-image doesn't cooperate with border-radius).
 */
/**
 * Card fill alpha (0.0–1.0). Applied to BOTH gradient stops on the
 * padding-box background layer so the tint fades from translucent tint
 * on the left to translucent white on the right. Change this single
 * value to test alpha effects across every status card at once:
 *   0.0 → invisible / fully see-through
 *   0.2 → barely there
 *   0.4 → Figma spec (default)
 *   0.6 → clearly tinted, page still faintly visible
 *   1.0 → fully opaque, matches the old solid-tint look
 */
export const CARD_FILL_ALPHA = 0.4;

// Compact "R, G, B" strings — expanded once and reused per status.
// Kept without the "rgba(" prefix so buildCardStyle can splice in the
// current CARD_FILL_ALPHA on every render without string-editing hex.
const RGB = {
  OPTIMAL: '224, 250, 236',    // #E0FAEC
  ACCEPTABLE: '255, 243, 235', // #FFF3EB
  FLAGGED: '255, 235, 236',    // #FFEBEC
  WHITE: '255, 255, 255',
};

/**
 * Two style objects per factor card:
 *   - `body` → applied to the OUTER card element. Just the translucent
 *     fill gradient. No border — the page background shows through
 *     wherever the alpha allows.
 *   - `borderRing` → applied to an absolutely-positioned pseudo layer
 *     inside the card. Paints the gradient as a 1px ring around the
 *     card's perimeter via a mask cutout, so the interior of the ring
 *     stays clean and the outer card's alpha isn't corrupted by an
 *     opaque underlay (the bug in the earlier `background-clip: …
 *     border-box` approach — that clip only picks WHERE each layer
 *     paints, but the layers still stack, so a translucent top layer
 *     revealed the opaque border-gradient across the whole interior).
 *
 * The mask trick: paint the entire ring div with the gradient, then
 * mask out everything inside content-box (the box shrunk by the ring's
 * padding). `mask-composite: exclude` cuts the inner mask from the
 * outer one, leaving only the ring visible. Both `-webkit-` and
 * standard properties included for cross-browser reach.
 */
const buildCardStyle = ({ tintRgb, borderFrom, borderTo }) => ({
  body: {
    // Two-layer background stack. CSS paints comma-separated `background`
    // values top-to-bottom, so the tint gradient sits ON TOP of a solid
    // white base. Result:
    //   alpha 0.0 → tint is invisible, card reads as pure white
    //   alpha 0.4 → light tint blended against white (Figma default)
    //   alpha 1.0 → tint fully opaque, matches the old solid look
    // Because the base is opaque white, CARD_FILL_ALPHA now controls
    // TINT STRENGTH — not "how much page shows through". Nothing behind
    // the card can bleed in anymore.
    background: [
      `linear-gradient(90deg, rgba(${tintRgb}, ${CARD_FILL_ALPHA}) 0%, rgba(${RGB.WHITE}, ${CARD_FILL_ALPHA}) 100%)`,
      '#FFFFFF',
    ].join(', '),
  },
  borderRing: {
    background: `linear-gradient(90deg, ${borderFrom} 0%, ${borderTo} 100%)`,
    padding: '1px',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
  },
});

export const STATUS_STYLES = {
  [STATUS.OPTIMAL]: {
    cardBg: 'bg-green-50',
    cardBorder: 'border-green-100',
    // Inline-style background — Tailwind can't express two background
    // layers with different `background-clip` regions as class atoms.
    cardStyle: buildCardStyle({
      tintRgb: RGB.OPTIMAL,
      borderFrom: '#C2F5DA',
      borderTo: '#EBEBEB',
    }),
    // Pill uses the same tint family as the card gradient's left stop
    // (#E0FAEC) so the pill reads as a concentrated slice of the card
    // color, not a competing accent. The icon inherits text color via
    // currentColor — one hex covers both text + icon per Figma.
    pillBg: 'bg-[#E0FAEC]',
    pillText: 'text-[#1FC16B]',
    label: 'Optimal',
    // Icon key — factor card resolves to a remixicon by name.
    iconKey: 'check',
  },
  [STATUS.ACCEPTABLE]: {
    cardBg: 'bg-amber-50',
    cardBorder: 'border-amber-100',
    cardStyle: buildCardStyle({
      tintRgb: RGB.ACCEPTABLE,
      borderFrom: '#FFD9C0',
      borderTo: '#EBEBEB',
    }),
    pillBg: 'bg-[#FFF3EB]',
    pillText: 'text-[#FA7319]',
    label: 'Acceptable',
    iconKey: 'alert',
  },
  [STATUS.FLAGGED]: {
    cardBg: 'bg-red-50',
    cardBorder: 'border-red-100',
    cardStyle: buildCardStyle({
      tintRgb: RGB.FLAGGED,
      borderFrom: '#FFC0C5',
      borderTo: '#EBEBEB',
    }),
    pillBg: 'bg-[#FFEBEC]',
    pillText: 'text-[#FB3748]',
    label: 'Flagged',
    iconKey: 'error',
  },
};

/**
 * Flatten the API's tail_breakdown + fleet_breakdown component maps
 * into a single ordered array of card-ready records. Consumers render
 * this array 1:1 as factor cards.
 *
 * Order: all tail components first (IHT, MRT, OST) followed by fleet
 * components (CSF, FSF, ORF) — matches the design's card sequence and
 * groups related metrics together.
 */
export const flattenBreakdownComponents = (factors) => {
  const out = [];
  const pushGroup = (breakdown, group) => {
    const comps = breakdown?.components;
    if (!comps || typeof comps !== 'object') return;
    for (const [key, comp] of Object.entries(comps)) {
      out.push({
        key,
        group, // 'tail' | 'fleet'
        title: getComponentTitle(key, comp?.description),
        body: getComponentBody(comp?.description),
        value: comp?.value,
        status: getComponentStatus(key, comp?.value),
      });
    }
  };
  pushGroup(factors?.tail_breakdown, 'tail');
  pushGroup(factors?.fleet_breakdown, 'fleet');
  return out;
};

/**
 * Auto-generate a short risk narrative from the flattened components.
 * Used on the hero card since the API doesn't return a summary string.
 * Wording tuned to the worst status present so the sentence matches
 * the pill color the user's eye is drawn to first.
 */
export const buildRiskNarrative = (flatComponents) => {
  if (!Array.isArray(flatComponents) || flatComponents.length === 0) return '';
  const anyFlagged = flatComponents.some((c) => c.status === STATUS.FLAGGED);
  const anyAcceptable = flatComponents.some((c) => c.status === STATUS.ACCEPTABLE);
  if (anyFlagged) {
    return 'High Risk. One or more metrics are flagged — review the breakdown below and consider mitigations before booking.';
  }
  if (anyAcceptable) {
    return 'Moderate Risk. Most metrics are stable; a few areas are within acceptable but not optimal range.';
  }
  return 'Low Risk. A solid operator with clean history and metrics that all fall within the optimal range.';
};

/**
 * Left-column "summary tiles" derived from tail_score / fleet_score.
 * Purely visual — matches the design's Excellent / Caution pair.
 */
export const getTileVariant = (score) => {
  const v = Number(score);
  if (!Number.isFinite(v)) return { label: 'Unavailable', tone: 'gray', iconKey: 'info' };
  if (v >= 90) return { label: 'Excellent', tone: 'green', iconKey: 'star' };
  if (v >= 70) return { label: 'Good', tone: 'blue', iconKey: 'check' };
  return { label: 'Caution', tone: 'amber', iconKey: 'alert' };
};
